import { motion } from 'framer-motion'
import { AlertCircle, Home, RefreshCcw } from 'lucide-react'
import { useRouter } from 'next/router'
import React from 'react'
import { useSelector } from 'react-redux'
import { Button } from '~/components/Common/Button'
import { Typography } from '~/components/Common/Typography'
import locale from '~/locale'
import { RootState } from '~/redux/store'

interface ErrorFallbackProps {
    statusCode?: number
    message?: string
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ statusCode, message }) => {
    const router = useRouter()
    const language = useSelector((state: RootState) => state.language.language)
    const localeText = (locale[language as keyof typeof locale] || locale.en) as any



    const handleRefresh = () => {
        window.location.reload()
    }

    const handleGoHome = () => {
        window.location.href = '/business/dashboard'
    }

    return (
        <div className="flex  border-[3px] flex-col items-center justify-center min-h-screen p-6 text-center">
            <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="mb-8 relative"
            >
                <div className="absolute inset-0 bg-red-100 rounded-full blur-3xl opacity-30 animate-pulse" />
                <div className="relative bg-white p-6 rounded-3xl shadow-xl border border-red-50">
                    <AlertCircle size={40} className="text-red-500" />
                </div>
            </motion.div>

            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
            >
                <Typography variant="h1" size="xl" fontWeight="bold" className="mb-4 text-neutrals-900">
                    {statusCode === 404 ? localeText?.common?.error?.not_found : localeText?.common?.error?.title}
                </Typography>

                <Typography variant="p" size="sm" className="mb-8 text-neutrals-500 max-w-md mx-auto">
                    {message || localeText?.common?.error?.description}
                </Typography>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <div className="shadow-lg shadow-orange-200 rounded-lg">
                        <Button
                            text={localeText?.common?.error?.refresh}
                            onClick={handleRefresh}
                            icon={<RefreshCcw size={18} />}
                            iconPosition="left"
                            size="lg"
                        />
                    </div>
                    <Button
                        text={localeText?.common?.error?.go_back}
                        onClick={handleGoHome}
                        type="outlined"
                        icon={<Home size={18} />}
                        iconPosition="left"
                        size="lg"
                    />
                </div>
            </motion.div>

            {statusCode && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.5 }}
                    transition={{ delay: 1 }}
                    className="mt-12 text-sm text-neutrals-400 font-medium"
                >
                    Error Code: {statusCode}
                </motion.div>
            )}
        </div>
    )
}

export default ErrorFallback
